import React, { useContext } from 'react'
import styled from 'styled-components'
import { CollectionContext } from 'shared/components/context/add-to-collections-manager'
import { giphyWhite, giphyBlue, giphyBlack } from '@giphy/js-brand'
import { getWebp } from 'shared/util/gif-extract'
import { removeGif } from 'shared/api/channels'
import ModalContext from 'shared/components/context/modal'
import MessageContext from 'shared/components/context/message'

const Container = styled.div<{ width?: number; height?: number; top?: number }>`
    width: ${props => props.width}px;
    height: ${props => (props.height ? `${props.height}px` : '100%')};
    position: absolute;
    z-index: 2;
    top: ${props => props.top && `${props.top}px`};
`

const IconCheck = styled.span`
    color: rgb(0, 230, 204);
    background: black;
    height: 20px;
    width: 20px;
    font-size: 9px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    margin-right: 4px;

    &:before {
        margin-top: 2px;
    }
`

const IconUndo = styled.span`
    margin-left: 10px;
    margin-bottom: -3px;
`

const SuccessMessage = styled.div`
    display: flex;
    align-items: center;
    height: 52px;
    width: 100%;
    position: absolute;
    top: 0;
    left: 0;
    padding: 15px;
    background: linear-gradient(-135deg, rgb(0, 230, 204) 0%, rgb(0, 255, 153) 100%);
    border-radius: 4px 4px 0px 0px;
    box-shadow: 0px 10px 20px 0px rgba(0, 0, 0, 0.4);
    color: ${giphyBlack};
    font-size: 15px;
    b {
        font-weight: bold;
    }
`

const UndoButton = styled.div`
    position: absolute;
    display: flex;
    align-items: center;
    right: 0;
    padding: 15px;
    font-weight: bold;
    cursor: pointer;
`

const GifContainer = styled.div`
    position: absolute;
    bottom: 12px;
    right: 12px;
`

const Item = styled.div<{ gutter?: boolean }>`
    cursor: pointer;
    width: 153px;
    cursor: pointer;
    display: flex;
    flex-direction: column;
    position: relative;
`

const ItemGif = styled.img`
    height: 88px;
    width: 100%;
    object-fit: cover;
    border-radius: 6px 6px 0px 0px;
`

const ItemText = styled.div`
    position: relative;
    border-radius: 0px 0px 6px 6px;
    height: 30px;
    font-size: 15px;
    font-weight: bold;
    color: ${giphyWhite};
    padding: 5px 10px 8px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    background: ${giphyBlue};
`

type CollectionsSuccessMessageProps = {
    width?: number
    height?: number
    top?: number
    undoAction?: () => void
    className?: any
    showGif?: boolean
}

const CollectionsSuccessMessage = ({
    width,
    height,
    top,
    undoAction,
    className = '',
    showGif,
}: CollectionsSuccessMessageProps) => {
    const {
        addGifToCollectionSuccessMessage,
        addGifsToCollectionSuccessMessage,
        collectionCreatedSuccessMessage,
        setAddGifToCollectionSuccessMessage,
        combineCollectionsSuccessMessage,
    } = useContext(CollectionContext)
    const { sendMessage } = useContext(MessageContext)
    const { openCollectionCabinet } = useContext(ModalContext)
    const { setGif } = useContext(CollectionContext)

    const removeGifFromCollection = () => {
        if (addGifToCollectionSuccessMessage) {
            removeGif({
                gif: addGifToCollectionSuccessMessage.featuredGif.id,
                channel: addGifToCollectionSuccessMessage?.channelId,
            })
            setAddGifToCollectionSuccessMessage(undefined)
            openCollectionCabinet()
            setGif(addGifToCollectionSuccessMessage.featuredGif)
            sendMessage('GIF removed from collection', 'info')
        }
    }

    if (
        !addGifToCollectionSuccessMessage &&
        !combineCollectionsSuccessMessage &&
        !addGifsToCollectionSuccessMessage &&
        !collectionCreatedSuccessMessage
    ) {
        return <></>
    }

    const DefaultMessage = () => {
        if (combineCollectionsSuccessMessage || addGifToCollectionSuccessMessage || addGifsToCollectionSuccessMessage) {
            return (
                <span>
                    <b>{combineCollectionsSuccessMessage?.draggableName || null}</b> Added to{' '}
                    <b>
                        {' '}
                        {combineCollectionsSuccessMessage?.targetChannelName ||
                            addGifToCollectionSuccessMessage?.channelName ||
                            addGifsToCollectionSuccessMessage?.channelName}
                    </b>
                </span>
            )
        }
        return <></>
    }

    const CreatedCollectionsMessage = () => {
        if (collectionCreatedSuccessMessage) {
            return (
                <span>
                    <b>{collectionCreatedSuccessMessage.channelName} succefully created</b>
                </span>
            )
        }
        return <></>
    }

    const gif = addGifToCollectionSuccessMessage && getWebp(addGifToCollectionSuccessMessage.featuredGif)

    return (
        <Container width={width} height={height} top={top} className={className}>
            <SuccessMessage>
                <IconCheck className="ss-check" />
                <DefaultMessage />
                <CreatedCollectionsMessage />
                <UndoButton onClick={undoAction || removeGifFromCollection}>
                    Undo
                    <IconUndo className="ss-replay" />
                </UndoButton>
            </SuccessMessage>
            {!!addGifToCollectionSuccessMessage && gif && showGif && (
                <GifContainer>
                    <Item>
                        <ItemGif src={gif} alt={addGifToCollectionSuccessMessage.channelName} />
                        <ItemText>{addGifToCollectionSuccessMessage.channelName}</ItemText>
                    </Item>
                </GifContainer>
            )}
        </Container>
    )
}

export default CollectionsSuccessMessage
